import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Função para verificar se o token está próximo do vencimento
  const isTokenNearExpiry = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;
      
      // Se faltam menos de 24 horas para expirar, renovar
      return timeUntilExpiry < (24 * 60 * 60);
    } catch (error) {
      return true; // Se houver erro ao ler o token, assumir que precisa renovar
    }
  };

  // Função para renovar o token
  const refreshToken = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return false;

      const response = await fetch(API_ENDPOINTS.REFRESH_TOKEN, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Atualizar token e dados do usuário
        localStorage.setItem('token', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        localStorage.setItem('phone', data.phone);
        localStorage.setItem('isAdmin', data.isAdmin ? 'true' : 'false');
        
        setUser({
          username: data.username,
          email: data.email,
          phone: data.phone,
          isAdmin: data.isAdmin
        });
        
        setIsAuthenticated(true);
        return true;
      } else {
        // Token inválido ou expirado, fazer logout
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('email');
        localStorage.removeItem('phone');
        localStorage.removeItem('isAdmin');
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('email');
      localStorage.removeItem('phone');
      localStorage.removeItem('isAdmin');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  }, []);

  // Função para fazer logout
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('phone');
    localStorage.removeItem('isAdmin');
    
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Função para fazer login
  const login = useCallback(async (identifier, password) => {
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier, password })
      });

      const data = await response.json();

      if (response.ok && data.status === 'ok') {
        // Salvar dados no localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        localStorage.setItem('phone', data.phone);
        localStorage.setItem('isAdmin', data.isAdmin ? 'true' : 'false');
        
        setUser({
          username: data.username,
          email: data.email,
          phone: data.phone,
          isAdmin: data.isAdmin
        });
        
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Erro no login' };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      return { success: false, error: 'Erro de conexão' };
    }
  }, []);

  // Verificar autenticação ao inicializar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Verificar se o token está próximo do vencimento
        if (isTokenNearExpiry(token)) {
          const renewed = await refreshToken();
          if (!renewed) {
            setLoading(false);
            return;
          }
        } else {
          // Token ainda válido, carregar dados do usuário
          const username = localStorage.getItem('username');
          const email = localStorage.getItem('email');
          const phone = localStorage.getItem('phone');
          const isAdmin = localStorage.getItem('isAdmin') === 'true';
          
          setUser({ username, email, phone, isAdmin });
          setIsAuthenticated(true);
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, [refreshToken]);

  // Verificar periodicamente se o token precisa ser renovado
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token && isTokenNearExpiry(token)) {
        await refreshToken();
      }
    }, 60 * 60 * 1000); // Verificar a cada hora

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshToken]);

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    refreshToken
  };
};

export default useAuth;
